// Email-level rendering — the orchestrator. Per spec/tech-arch.md §3.2 +
// §3.3 + §11.5 + §11.6 + spec/build-plan.md P-019 row.
//
// **Pipeline (Tech Arch §11.6 8-step algorithm):**
// 1. Walk `email.blocks` once via `prepareBlock` (D-079) — lookup +
//    skip-included + parse + skip-autoHide. Cache the prepared result so
//    we don't double-parse during the render pass.
// 2-4. Owned by `prepareBlock` (per D-077 + D-079).
// 5. Backward-pass O(n) right-to-left walk to compute
//    `followingBlocksAllHidden` per block. A block at index `j > i` is
//    "hidden" iff its `prepareBlock` returned `{kind: 'skip'}`. The
//    backward sweep maintains a single `allHiddenSoFar` boolean and
//    snapshots it BEFORE updating with the current block's own
//    skip-state. section-heading.ts (D-052) consumes this field.
// 6-7. Owned by `def.render({options, inputs}, ctx)` per BlockDefinition
//    contract (Tech Arch §11.4).
// 8. Wrap the concatenated body HtmlString in `documentShell({title,
//    body})` and return `.value` as plain string. The document boundary
//    is where the HtmlString brand "unwraps" per Tech Arch §3.2.
//
// **Email type.** EmailSchema (Tech Arch §2.5) is the canonical Zod
// schema; `z.infer<typeof EmailSchema>` gives the parsed-output shape
// with `included: boolean` (non-optional, post-`.default(true)`
// resolution). The renderer at this layer reads only `name` (document
// `<title>`) and `blocks` (the walk).
//
// **Empty-Email edge case.** If `email.blocks` is empty OR every block's
// `prepareBlock` returns `{kind:'skip'}`, the body is `EMPTY_HTML` (D-080)
// and the document shell wraps an empty body. Returns a valid HTML5
// document, not a partial fragment.
//
// **`for…of [...entries()].reverse()`** is the chosen iteration form
// (over an indexed `for (let i = …)` loop) to avoid `noUncheckedIndexed-
// Access` (D-006) introducing dead `?? true` / `?.kind` branches that
// would fail the 100% coverage gate (D-077). `entries()` yields
// `[number, PreparedBlock]` tuples with no `undefined` arm.
//
// **Light-only output (D-100, P-037 close).** The email-output is a
// single light palette. Earlier `colorScheme` opts-arg + dark-mode
// `[data-color-scheme="dark"]` shell branching was removed alongside
// the half-implemented dark-mode `@media` rule.
//
// **`isolated` opt (P-037.7 / D-107).** Optional second arg added back
// after D-100 dropped the prior `colorScheme` opt. When `opts.isolated`
// is `true`, the backward pass seeds `allHiddenSoFar = false` instead
// of `true` — so every block sees `followingBlocksAllHidden: false`
// regardless of its position. Used by the Block Gallery (block-
// preview.tsx) where a single-block synthetic envelope would otherwise
// trigger `section-heading`'s dangle-prevention auto-hide vacuously
// (all 0 following blocks are trivially "all hidden"). Production
// callers (email-editor, output-toolbar, render-email tests) pass no
// opts → behavior unchanged.

import { interBlockSpacerHeight } from './block-padding'
import { documentShell } from './document-shell'
import { html } from './html'
import { prepareBlock, type PreparedBlock } from './render-block'
import {
  CTA_BG_DEFAULT,
  STYLE_CTA_BG,
  STYLE_LINK_COLOR,
  STYLE_SUBHEADER,
  STYLE_TINT_10,
  TINT_DEFAULT,
} from '../blocks/header'
import type { Email } from '../store/schemas'
import { tokens } from '../tokens'
import { EMPTY_HTML, type BlockCategory, type HtmlString, type RenderContext } from '../types'

interface PreparedWithCtx {
  prepared: PreparedBlock
  ctx: RenderContext
}

export interface RenderEmailOptions {
  /**
   * When `true`, render assumes each block has visible content following
   * it — `RenderContext.followingBlocksAllHidden` is `false` for every
   * block regardless of position. Used by the Block Gallery for isolated
   * single-block previews so `section-heading`'s dangle-prevention auto-
   * hide (D-052) doesn't fire vacuously when the synthetic envelope has
   * only one block. Production renders MUST NOT pass this — the gating
   * is correct behavior for real emails. Default: `false`.
   */
  readonly isolated?: boolean
}

/**
 * P-037.18 D-118 / P-037.19 D-125 / P-037.23 D-131 / P-037.28 D-136 —
 * single first-included-Header walk that derives every template-level
 * `RenderContext` field driven by Header style (linkColor,
 * subheaderImageUrl, tintColor, ctaBgColor). The four derivations
 * previously lived in parallel functions; unified at P-037.23 since
 * they share the exact walk + narrowing pattern and differ only in
 * the lookup table + per-derivation fallback. ctaBgColor folded in at
 * P-037.28 as the fourth use of the pattern.
 *
 * Each fallback is chosen per-derivation:
 * - linkColor: `tokens.color['link-default']` (#c69b34) — gold.
 * - subheaderImageUrl: `STYLE_SUBHEADER.signal` — Banks: "Signal by
 *   default". A concrete asset URL is needed; we can't use the empty
 *   string.
 * - tintColor: `TINT_DEFAULT` (#f9f5eb) — the 10% blend of link-default
 *   over white. Pre-computed hex per Banks (`rgba()` unreliable in email).
 * - ctaBgColor: `CTA_BG_DEFAULT` (#162443 navy) — matches the pre-D-136
 *   text-block button bg.
 *
 * A header that's excluded (`included: false`) returns `{kind: 'skip'}`
 * from prepareBlock and is correctly skipped by the `kind !== 'render'`
 * guard. `p.options` is widened to `any` per the PreparedBlock contract;
 * the narrowing cast is purely for the lookup-index typing — Header's
 * optionsSchema guarantees `style` is one of the 7 enum values.
 */
function deriveHeaderStyleContext(prepared: ReadonlyArray<PreparedBlock>): {
  linkColor: string
  subheaderImageUrl: string
  tintColor: string
  ctaBgColor: string
} {
  for (const p of prepared) {
    if (p.kind !== 'render') continue
    if (p.def.id !== 'header') continue
    const style = (p.options as { style: keyof typeof STYLE_LINK_COLOR }).style
    return {
      linkColor: STYLE_LINK_COLOR[style],
      subheaderImageUrl: STYLE_SUBHEADER[style],
      tintColor: STYLE_TINT_10[style],
      ctaBgColor: STYLE_CTA_BG[style],
    }
  }
  return {
    linkColor: tokens.color['link-default'],
    subheaderImageUrl: STYLE_SUBHEADER.signal,
    tintColor: TINT_DEFAULT,
    ctaBgColor: CTA_BG_DEFAULT,
  }
}

export function renderEmail(email: Email, opts: RenderEmailOptions = {}): string {
  const prepared: PreparedBlock[] = email.blocks.map(prepareBlock)
  const totalBlocks = prepared.length
  const { linkColor, subheaderImageUrl, tintColor, ctaBgColor } =
    deriveHeaderStyleContext(prepared)

  // Backward pass: compute `followingBlocksAllHidden` per block. Build
  // PreparedWithCtx pairs in reverse, then reverse at the end so the
  // forward render pass sees them in original order.
  const reverseItems: PreparedWithCtx[] = []
  let allHiddenSoFar = !opts.isolated
  for (const [i, p] of [...prepared.entries()].reverse()) {
    reverseItems.push({
      prepared: p,
      ctx: {
        index: i,
        totalBlocks,
        isLast: i === totalBlocks - 1,
        followingBlocksAllHidden: allHiddenSoFar,
        linkColor,
        subheaderImageUrl,
        tintColor,
        ctaBgColor,
      },
    })
    if (p.kind !== 'skip') allHiddenSoFar = false
  }
  const items = reverseItems.reverse()

  // Forward render pass: skip blocks marked `skip`, render the rest with
  // their precomputed contexts. Collect both the rendered HTML and the
  // BlockDefinition so the inter-Block spacer pass below can read each
  // Block's `category` + `id` for padding lookup (P-037.29 D-137 §item 2).
  //
  // **P-037.31 fourth post-Phase-Close §D — `ctx.precedingBlockId`.**
  // The forward pass tracks the immediately-preceding RENDERED Block's
  // id (skipped Blocks excluded) and spreads it into a per-iteration
  // ctx so each Block's render call sees its true visible predecessor.
  // The standalone `sub-section-heading` Block reads this field to
  // auto-suppress its top rule when the preceding Block is
  // `big3-signature-events`. Computed here (not in the backward pass
  // that builds the base ctx) because skipped Blocks must not count —
  // and the backward pass walks `prepared` indiscriminately.
  interface RenderedBlock {
    readonly html: HtmlString
    readonly category: BlockCategory
    readonly id: string
  }
  const rendered: RenderedBlock[] = []
  let precedingBlockId: string | undefined
  for (const { prepared: p, ctx } of items) {
    if (p.kind === 'skip') continue
    const ctxWithPreceding: RenderContext =
      precedingBlockId === undefined ? ctx : { ...ctx, precedingBlockId }
    rendered.push({
      html: p.def.render({ options: p.options, inputs: p.inputs }, ctxWithPreceding),
      category: p.def.category,
      id: p.def.id,
    })
    precedingBlockId = p.def.id
  }

  // P-048.9 / D-152 — Body content emitted as ROWS of a SINGLE outer
  // wrapping `<table bgcolor="#ffffff">`. The inter-Block gap is now
  // baked into each Block-row cell as `padding-bottom: ${gap}px;`
  // (NOT a sibling spacer row as in the P-048.8 / D-151 design). This
  // is the refined fix for Outlook Desktop's gray-bands bug. P-048.8
  // discovered that Outlook's bg cascade WITHIN a single table is
  // unreliable for cells with NO substantial content — the spacer's
  // `<td>&nbsp;</td>` with `line-height: 0; font-size: 0;` is treated
  // as a height-only strut and Outlook skips the bg paint, leaking
  // body bg through despite the parent table's `bgcolor="#ffffff"`.
  // Banks's third Outlook smoke (after P-048.8 deploy) STILL showed
  // gray bands; the cascade-to-contentless-cell rule was the missed
  // mechanism.
  //
  // Refined fix: move the gap INSIDE the Block-row cell as `padding-
  // bottom`. The cell contains substantial nested-table content (the
  // Block's `<table>...</table>`), so Outlook reliably cascade-paints
  // the cell white from the wrapper table's `bgcolor`. The padding-
  // bottom area is INSIDE the same cell, so it inherits the cell's
  // white bg without any contentless-cell quirk.
  //
  // Net rendered HTML: each Block row gains `style="padding-bottom:
  // ${gap}px;"` (the last Block row has no padding-bottom). The
  // separate spacer rows are GONE.
  //
  // Spacing preservation: total height of `Block N (height_N) + gap
  // (padding-bottom_N) + Block N+1 (height_N+1)` is byte-identical
  // to the pre-P-048.9 design. `interBlockSpacerHeight()` lookup
  // unchanged. PAIR_OVERRIDES (collapse-to-0) preserved: when gap
  // === 0, the cell emits no padding-bottom (or padding-bottom: 0px;
  // — visually identical). `isLast`-based skip-trailing-spacer
  // preserved: the last Block's row has no padding-bottom.
  const wrapperRows: HtmlString[] = []
  for (let i = 0; i < rendered.length; i++) {
    const cur = rendered[i]!
    const isLast = i === rendered.length - 1
    const gap = isLast
      ? 0
      : interBlockSpacerHeight(cur.category, cur.id, rendered[i + 1]!.category, rendered[i + 1]!.id)
    // prettier-ignore
    wrapperRows.push(
      gap > 0
        ? html`<tr><td style="padding-bottom: ${gap}px;">${cur.html}</td></tr>`
        : html`<tr><td>${cur.html}</td></tr>`,
    )
  }

  // Empty Email (no rendered Blocks) → no wrapper table emitted; body
  // is empty per pre-P-048.8 EMPTY_HTML behavior. Avoids emitting an
  // empty `<table>` with no rows (Outlook quirk safety).
  const body =
    wrapperRows.length === 0
      ? EMPTY_HTML
      : // prettier-ignore
        html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" bgcolor="#ffffff" style="background-color: #ffffff;">${wrapperRows}</table>`

  return documentShell({ title: email.name, body }).value
}

/**
 * **P-048.9 / D-152 — spacerRow function REMOVED.** Inter-Block gap is
 * now emitted as `padding-bottom: ${gap}px;` on each Block-row's `<td>`
 * in the body wrapper table (see the wrapperRows construction in
 * `renderEmail` above). The padding sits inside a cell with substantial
 * content (the Block's `<table>...</table>`), so Outlook cascade-paints
 * the cell white from the wrapper's `bgcolor="#ffffff"` — fixing the
 * gray-bands bug that P-048.6 / D-149 + P-048.7 / D-150 + P-048.8 /
 * D-151 all failed to resolve.
 *
 * **Cumulative pre-P-048.9 history (preserved for context):**
 *
 * - P-037.29 / D-137 §item 2: original standalone-table inter-Block
 *   spacer pattern. Outlook desktop's Word renderer painted body bg
 *   `#f1f2f5` behind the transparent spacer tables (nested-table-
 *   cascade quirk).
 *
 * - P-048.6 §C / D-149: added `bgcolor="#ffffff"` to (a) the 680
 *   wrapper inner `<td>` and (b) each spacer cell. Didn't work in
 *   Outlook — the renderer skips bg paint on contentless cells.
 *
 * - P-048.7 / D-150: MSO conditional `<style>` body bg override.
 *   Outlook ignored the CSS body bg rule inside the conditional.
 *
 * - P-048.8 / D-151: refactored to single body-wrapper table with all
 *   Blocks + spacers as ROWS (eliminating the nested-table boundary
 *   for spacers). Wrapper `<table bgcolor="#ffffff">` should have
 *   cascaded white to spacer rows via single-table cascade. STILL
 *   failed — Outlook skips cascade-to-cell when the cell has no
 *   substantial content (contentless `<td>&nbsp;</td>` with line-
 *   height: 0 + font-size: 0 reads as height-only strut, bg paint
 *   skipped).
 *
 * - P-048.9 / D-152 (CURRENT): move gap INSIDE each Block-row's cell
 *   as `padding-bottom: ${gap}px;`. The cell contains the Block's
 *   substantial HTML; Outlook cascade-paints the whole cell (including
 *   padding-bottom area) white. No contentless cells in the body
 *   structure anymore. Spacing preserved byte-for-byte
 *   (`interBlockSpacerHeight()` registry lookup unchanged; total height
 *   of `Block + gap + next Block` identical).
 */
